import React, { useMemo, useEffect, useRef } from "react";
import { Box, Image } from "@mantine/core";
import { Venue, Display, DisplayRunningMessage } from "@/utils/api";
import { Section } from "@/config/layoutConfig";
import { TimeComponent } from "@/components/generic";
import { VenueNameComponent } from "../VenueNameComponent";
import { RoomNameComponent } from "../RoomNameComponent";
import { DisplayNameComponent } from "../DisplayNameComponent";
import { DisplayBlockRunningMessage } from "@/components/features/DisplayBlockRunningMessage";

export interface HeaderFooterSectionProps {
  /** Section configuration from layout config */
  section: Section;

  /** Type of section - determines which theme config to apply */
  sectionType: "header" | "footer";

  /** Venue data for logo and name */
  venue?: Venue;

  /** Display data */
  display?: Display;

  /** Room name to display */
  roomName?: string;

  /** Running messages for the footer */
  displayRunningMessages?: DisplayRunningMessage[];

  /** Theme configuration */
  themeConfig?: any;

  /** Additional CSS class name for styling */
  className?: string;

  loading?: boolean;

  isOffline?: boolean;
}

export const HeaderFooterSection: React.FC<HeaderFooterSectionProps> = ({
  section,
  sectionType,
  venue,
  display,
  roomName,
  displayRunningMessages,
  themeConfig,
  className,
  loading,
  isOffline,
}) => {
  // Generate unique animation name for this component instance
  const animationId = useMemo(() => {
    return `hf-pulse-${sectionType}-${Math.random().toString(36).substr(2, 9)}`;
  }, [sectionType]);

  const styleElementRef = useRef<HTMLStyleElement | null>(null);

  // Add style to head once and clean up on unmount
  useEffect(() => {
    if (!loading) return;

    // Only create style element if it doesn't exist
    if (!styleElementRef.current) {
      const styleElement = document.createElement("style");
      styleElement.textContent = `
        @keyframes ${animationId} {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 1; }
        }
      `;
      document.head.appendChild(styleElement);
      styleElementRef.current = styleElement;
    }

    // Cleanup on unmount
    return () => {
      if (styleElementRef.current) {
        document.head.removeChild(styleElementRef.current);
        styleElementRef.current = null;
      }
    };
  }, [loading, animationId]);

  // Don't render if height is 0
  if (section.height === "0rem") {
    return null;
  }

  // Get visible components
  const visibleComponents = useMemo(() => {
    return section.components.filter((component) => component.visible);
  }, [section.components]);

  // Check if there are any important running messages
  const hasImportantMessage = useMemo(() => {
    if (!displayRunningMessages || displayRunningMessages.length === 0) {
      return false;
    }
    return displayRunningMessages.some((msg) => msg.runningMessage?.importance);
  }, [displayRunningMessages]);

  // Get section-specific theme styling
  const sectionThemeStyle = useMemo(() => {
    if (!themeConfig) return {};

    // Start with header or footer base styling
    const baseStyle =
      sectionType === "footer"
        ? themeConfig.runningMessages?.regular || {}
        : themeConfig[sectionType] || {};

    // If this is footer with running messages, apply running message styling
    if (
      sectionType === "footer" &&
      displayRunningMessages &&
      displayRunningMessages.length > 0 &&
      themeConfig.runningMessages
    ) {
      const messageStyle = hasImportantMessage
        ? themeConfig.runningMessages.important
        : themeConfig.runningMessages.regular;

      return {
        ...baseStyle,
        ...messageStyle,
      };
    }

    return baseStyle;
  }, [themeConfig, sectionType, displayRunningMessages, hasImportantMessage]);

  // Render individual component
  const renderComponent = (component: any, index: number) => {
    const key = `${component.type}-${index}`;

    switch (component.type) {
      case "logo":
        if (!venue?.logoUrl) return null;
        return (
          <Box
            key={key}
            style={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            <Image
              src={venue.logoUrl}
              alt={venue.name || "Venue logo"}
              fit="contain"
              style={{
                height: "100%",
                width: "auto",
                maxHeight: "100%",
              }}
            />
          </Box>
        );

      case "venue_name":
        return (
          <Box
            key={key}
            style={{
              display: "flex",
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            <VenueNameComponent
              venue={venue}
              display={display}
              fallback={component.fallback}
            />
          </Box>
        );

      case "room_name":
        return (
          <Box
            key={key}
            style={{
              display: "flex",
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            <RoomNameComponent
              roomName={roomName}
              display={display}
              fallback={component.fallback}
            />
          </Box>
        );

      case "display_name":
        return (
          <Box
            key={key}
            style={{
              display: "flex",
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            <DisplayNameComponent display={display} />
          </Box>
        );

      case "time":
        return (
          <Box
            key={key}
            style={{
              display: "flex",
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            <TimeComponent format={component.format || "h:mm A"} />
          </Box>
        );

      case "running_messages":
        if (!displayRunningMessages || displayRunningMessages.length === 0) {
          return null;
        }
        return (
          <Box
            key={key}
            style={{
              flexGrow: 1,
              flexShrink: 1,
              flexBasis: "0%",
              minWidth: 0,
              height: "100%",
              display: "flex",
              alignItems: "center",
            }}
          >
            <DisplayBlockRunningMessage
              displayRunningMessages={displayRunningMessages}
              themeConfig={themeConfig}
            />
          </Box>
        );

      default:
        return null;
    }
  };

  // Filter out null components after rendering
  const renderedComponents = visibleComponents
    .map((component, index) => renderComponent(component, index))
    .filter(Boolean);

  if (section.autoHide === true && renderedComponents.length === 0) {
    return null;
  }

  // Check if there's a running_messages component
  const hasRunningMessages = visibleComponents.some(
    (component) => component.type === "running_messages"
  );

  // Split components into left side (all except last) and right side (last one)
  const leftComponents =
    renderedComponents.length > 1
      ? renderedComponents.slice(0, -1)
      : renderedComponents;
  const rightComponent =
    renderedComponents.length > 1
      ? renderedComponents[renderedComponents.length - 1]
      : null;

  // Get text color for status indicator
  const getStatusColor = () => {
    return sectionThemeStyle?.color || "#000000";
  };

  console.log("=== hasRunningMessages", hasRunningMessages);

  return (
    <Box
      className={`header-footer-section header-footer-section--${sectionType} ${
        className || ""
      }`}
      style={{
        height: section.height,
        ...sectionThemeStyle,
        width: "100%",
        position: "relative",
        boxSizing: "border-box",
      }}
    >
      {/* Main content container */}
      <Box
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          height: "100%",
          width: "100%",
          padding: "0 1rem",
          boxSizing: "border-box",
        }}
      >
        {/* Left side: all components except the last one */}
        {leftComponents.length > 0 && (
          <Box
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: "0.5rem",
              height: "100%",
              flexGrow: hasRunningMessages ? 1 : 0,
              flexShrink: hasRunningMessages ? 1 : 0,
              minWidth: 0,
            }}
          >
            {leftComponents}
          </Box>
        )}

        {/* Spacer to push right component to the right when there are left components */}
        {leftComponents.length > 0 && rightComponent && (
          <Box style={{ width: "0.5rem", flexShrink: 0 }} />
        )}

        {/* Right side: last component with right alignment */}
        {rightComponent && (
          <Box
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              height: "100%",
              flexShrink: 0,
            }}
          >
            {rightComponent}
          </Box>
        )}
      </Box>

      {/* Status indicator - subtle, top right corner */}
      {(loading || isOffline) && (
        <Box
          style={{
            position: "absolute",
            top: "0.2rem",
            right: "0.2rem",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: "0.25rem",
            opacity: 0.75,
            pointerEvents: "none",
            zIndex: 10,
          }}
        >
          {loading && (
            <Box
              style={{
                width: "0.375rem",
                height: "0.375rem",
                borderRadius: "50%",
                backgroundColor: getStatusColor(),
                animation: `${animationId} 1.5s ease-in-out infinite`,
              }}
            />
          )}

          {isOffline && (
            <Box
              style={{
                fontSize: "0.625rem",
                color: getStatusColor(),
                fontWeight: 400,
                lineHeight: 1,
              }}
            >
              offline
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};
